/**
 * Shape a resolved experiment-service experiment satisfies, narrow enough to build a forced control from.
 *
 * Deliberately structural rather than importing a concrete generated experiment type — each caller
 * passes its own, and the return is narrowed back to it.
 *
 * Pinned to `experiment-service` on purpose. The forced control below is assembled from these fields
 * alone and asserted back to the caller's type, so a shape carrying extra required fields would come
 * back missing them with the type checker none the wiser — a growthbook experiment adds `featureKey`
 * and `variantKey`. Excluding those types here turns that into a compile error at the call site.
 */
type ForceableExperiment = {
	readonly type: 'experiment-service';
	readonly experimentName: string;
	readonly config: unknown;
	readonly variation: string;
	readonly treated: boolean;
	readonly fallback?: boolean;
	readonly fallbackReason?: string;
};

/**
 * Resolves an out-of-scope visitor to control **and marks it as a non-assignment**.
 *
 * The `fallback` flag is the load-bearing part: `fallback || override` is the platform's only signal for
 * "not a real assignment", and it's what suppresses `$exposure`. A bare `{ variation: 'control' }` would be
 * byte-identical to genuine control bucketing, so an out-of-scope visitor could be treated and exposed as
 * control, inflating that arm undetectably.
 *
 * `override` is deliberately not carried through: the resolved-experiment type treats it and `fallback` as
 * mutually exclusive, and the forced control is the caller's scope decision, not the override's.
 *
 * Not every normalizer in this folder uses this yet — some predate it and return a bare control without
 * the flag. Prefer this shape for new normalizers.
 */
export function forceControlOffScope<T extends ForceableExperiment>(experiment: T): T {
	return {
		type: experiment.type,
		experimentName: experiment.experimentName,
		config: experiment.config,
		variation: 'control',
		treated: false,
		fallback: true,
		// Keep a real upstream reason (e.g. `disabled` from the kill switch, or `fetch-error` from an
		// unreachable experiment service); claim `not-enrolled` only when the exclusion is the caller's.
		fallbackReason: experiment.fallback ? experiment.fallbackReason : 'not-enrolled',
	} as unknown as T;
}
